/**
 * TCC 选择性校核与多曲线叠加工具
 * 提供按绝对电流坐标(Isec)生成曲线、选择性校核与整定建议。
 */

// 曲线族注册表：包含 IEC 与 IEEE 常用反时限曲线，并支持扩展厂商曲线
const CURVE_FAMILY_REGISTRY = {
  canonical: {
    // IEC 60255（与 protectionCalculations 保持一致）
    NI:  { k: 0.14,  alpha: 0.02, family: 'IEC',  name: 'Normal Inverse' },
    VI:  { k: 13.5,  alpha: 1.0,  family: 'IEC',  name: 'Very Inverse' },
    EI:  { k: 80,    alpha: 2.0,  family: 'IEC',  name: 'Extremely Inverse' },
    LTI: { k: 120,   alpha: 1.0,  family: 'IEC',  name: 'Long-time Inverse' },
    // IEEE C37.112 常用曲线（以典型常数为参考值）
    IEEE_MI: { k: 0.0515, alpha: 0.02, family: 'IEEE', name: 'IEEE Moderately Inverse' },
    IEEE_VI: { k: 19.61,  alpha: 2.0,  family: 'IEEE', name: 'IEEE Very Inverse' },
    IEEE_EI: { k: 28.2,   alpha: 2.0,  family: 'IEEE', name: 'IEEE Extremely Inverse' }
  },
  synonyms: {
    // IEC 映射与别名
    'Normal Inverse': 'NI', 'IEC NI': 'NI', 'NI': 'NI',
    'Very Inverse': 'VI',  'IEC VI': 'VI', 'VI': 'VI',
    'Extremely Inverse': 'EI', 'IEC EI': 'EI', 'EI': 'EI',
    'Long-time Inverse': 'LTI', 'IEC LTI': 'LTI', 'LTI': 'LTI',
    // IEEE/ANSI 常见别名
    'Moderately Inverse': 'IEEE_MI', 'MI': 'IEEE_MI', 'ANSI MI': 'IEEE_MI', 'IEEE MI': 'IEEE_MI',
    'IEEE Very Inverse': 'IEEE_VI', 'ANSI VI': 'IEEE_VI', 'IEEE VI': 'IEEE_VI',
    'IEEE Extremely Inverse': 'IEEE_EI', 'ANSI EI': 'IEEE_EI', 'IEEE EI': 'IEEE_EI'
  }
};

export const registerCurveFamily = ({ key, family, name, k, alpha, synonyms = [] }) => {
  const canonKey = String(key || '').trim();
  if (!canonKey) return false;
  CURVE_FAMILY_REGISTRY.canonical[canonKey] = { k: Number(k), alpha: Number(alpha), family: String(family || 'custom'), name: String(name || canonKey) };
  (synonyms || []).forEach((s) => {
    const syn = String(s || '').trim();
    if (syn) CURVE_FAMILY_REGISTRY.synonyms[syn] = canonKey;
  });
  return true;
};

export const normalizeCurveType = (t) => {
  const raw = String(t || '').trim();
  const key = CURVE_FAMILY_REGISTRY.synonyms[raw];
  return key || 'NI';
};

export const getCurveConstants = (curveType) => {
  const canon = normalizeCurveType(curveType);
  return CURVE_FAMILY_REGISTRY.canonical[canon] || CURVE_FAMILY_REGISTRY.canonical.NI;
};

// 通用数值解析（支持可选单位后缀 A/kA/ms/s）
export const toNumber = (v) => {
  if (v === null || v === undefined) return NaN;
  if (typeof v === 'number') return v;
  const m = String(v).trim().match(/^([+-]?\d+(?:\.\d+)?)(?:\s*(A|kA|ms|s))?$/i);
  if (!m) return Number(v);
  const num = Number(m[1]);
  const unit = (m[2] || '').toLowerCase();
  if (unit === 'ka') return num * 1000;
  if (unit === 'ms') return num / 1000; // 仅用于时间
  return num;
};

// 统一电流单位为二次侧 A（支持 currentUnit 与 CT 变比）
export const normalizeCurrent = (val, currentUnit, ctRatio) => {
  let num = toNumber(val);
  if (!isFinite(num)) return NaN;
  const u = String(currentUnit || '').toLowerCase();
  if (u === 'ka') num *= 1000;
  if (u === 'a_primary' || u === 'primary') {
    const cr = Number(ctRatio);
    if (isFinite(cr) && cr > 0) num = num / cr; // 一次侧换算为二次侧
  }
  // 其他情况认为已是二次侧 A
  return num;
};

// 统一时间单位为秒（支持 ms/s）
export const normalizeTime = (val, timeUnit) => {
  let num = toNumber(val);
  if (!isFinite(num)) return NaN;
  const u = String(timeUnit || '').toLowerCase();
  if (u === 'ms') num = num / 1000;
  return num;
};

// 绝对电流坐标下的动作时间计算：Isec 为二次电流，pickup 为二次启动值
export const operateTimeByI = (Isec, pickup, TMS, curveType) => {
  if (!pickup || pickup <= 0) return Infinity;
  const c = getCurveConstants(curveType);
  const M = Isec / pickup;
  if (M <= 1) return Infinity;
  const t = TMS * c.k / (Math.pow(M, c.alpha) - 1);
  return +t.toFixed(3);
};

// 生成绝对坐标曲线：返回 [{I, t}] 数组
export const generateCurvePointsAbsolute = (pickup, TMS, curveType, Imin, Imax, steps = 60) => {
  const points = [];
  if (!pickup || !TMS || !curveType) return points;
  const start = Math.max(Imin || pickup * 1.1, pickup * 1.1);
  const end = Math.max(Imax || pickup * 20, start + 1e-3);
  const step = (end - start) / steps;
  for (let i = 0; i <= steps; i++) {
    const I = +(start + i * step).toFixed(2);
    const t = operateTimeByI(I, pickup, TMS, curveType);
    points.push({ I, t: +t });
  }
  return points;
};

// 构建曲线序列（含元数据）用于叠加
// 支持两种输入：
// 1) 旧接口：{ label, color, pickup, TMS, curveType }
// 2) 新统一结构：{ label, color, results } 或直接传入 results 对象
//    其中 results 包含 { pickupCurrent, timeMultiplier, curveType, curvePoints? }
export const buildSeries = (input, range = {}) => {
  const { Imin, Imax, steps } = range || {};

  // 解析 label/color
  const label = input?.label || input?.meta?.label || '曲线';
  const color = input?.color || input?.meta?.color || '#1677ff';

  let pickup; // 二次启动电流
  let TMS;    // 时间倍数
  let curveType;
  let points;
  let axesType; // I-t / Z / Id-Ir
  let meta;     // 其他结构化信息，如 zones/characteristic/lineAngleDeg

  // 兼容旧接口：直接提供 pickup/TMS/curveType
  if (Object.prototype.hasOwnProperty.call(input || {}, 'pickup') ||
      Object.prototype.hasOwnProperty.call(input || {}, 'TMS') ||
      Object.prototype.hasOwnProperty.call(input || {}, 'curveType')) {
    pickup = input.pickup;
    TMS = input.TMS;
    curveType = input.curveType;
    points = generateCurvePointsAbsolute(pickup, TMS, curveType, Imin, Imax, steps);
    axesType = 'I-t';
  } else {
    // 新统一结构：来自 protectionCalculations 的结果对象
    const results = input?.results || input || {};
    pickup = results?.pickupCurrent;
    TMS = results?.timeMultiplier;
    curveType = results?.curveType;
    axesType = results?.axesType || 'I-t';
    // 捕获结构化元数据
    if (Array.isArray(results?.zones)) meta = { zones: results.zones };
    if (results?.characteristic) meta = { ...(meta || {}), characteristic: results.characteristic };
    if (typeof results?.lineAngleDeg === 'number') meta = { ...(meta || {}), lineAngleDeg: results.lineAngleDeg };
    // 注入二次谐波闭锁系数（Kb）到叠加序列元数据，便于前端展示
    if (typeof results?.secondHarmonicBlocking === 'number' && isFinite(results.secondHarmonicBlocking)) {
      meta = { ...(meta || {}), secondHarmonicBlocking: results.secondHarmonicBlocking };
    }

    // 如已有按倍数 M 的曲线点，可转换为绝对坐标 I = M × pickup
    if (Array.isArray(results?.curvePoints) && results.curvePoints.length > 0) {
      const converted = results.curvePoints
        .map(p => {
          const M = p?.M;
          const t = p?.t;
          if (typeof M === 'number' && typeof t === 'number' && typeof pickup === 'number') {
            const I = +(M * pickup).toFixed(2);
            return { I, t: +t };
          }
          return null;
        })
        .filter(Boolean);
      points = converted && converted.length > 0 ? converted : undefined;
    }

    // 若无现成点或转换失败，回退到按绝对坐标生成
    if (!points || points.length === 0) {
      if (axesType === 'I-t') {
        points = generateCurvePointsAbsolute(pickup, TMS, curveType, Imin, Imax, steps);
      } else if (axesType === 'Z') {
        // 距离保护：将 zones 映射为简单线段点集（占位，可在 CurveOverlay 中按 zones 渲染形状）
        const zones = results?.zones || [];
        points = zones.map((z, idx) => ({ I: idx + 1, t: z?.Z })).filter(p => typeof p.t === 'number');
      } else if (axesType === 'Id-Ir') {
        // 差动特性：优先按 characteristic.segments 生成分段示意点（Ir → Id），否则退回单斜率近似
        const pickupDiff = results?.characteristic?.diffPickup ?? 0.4;
        const segments = Array.isArray(results?.characteristic?.segments) ? results.characteristic.segments : [];
        const hasSegs = segments.some(sg => typeof sg?.slope === 'number');
        const pts = [];
        const IrMax = Math.max(5, ...segments.map(sg => (isFinite(sg.threshold) ? Number(sg.threshold) : 0))) || 5;
        if (hasSegs) {
          // 按分段阈值构造采样点：每段 10 个点，连接确保连续
          let Ir_s = 0;
          let Id_s = pickupDiff;
          for (let si = 0; si < segments.length; si++) {
            const k = Number(segments[si].slope) || 0;
            const Ir_e_raw = segments[si].threshold;
            const Ir_e = Math.min(isFinite(Ir_e_raw) ? Ir_e_raw : IrMax, IrMax);
            if (Ir_e <= Ir_s) continue;
            const stepsLocal = 10;
            for (let j = 0; j <= stepsLocal; j++) {
              const frac = j / stepsLocal;
              const Ir = Ir_s + frac * (Ir_e - Ir_s);
              const Id = Id_s + k * (Ir - Ir_s);
              pts.push({ I: +(+Ir).toFixed(2), t: +(+Id).toFixed(3) });
            }
            // 更新至段末尾作为下段起点（确保连续）
            Ir_s = Ir_e;
            const Id_e = Id_s + k * (Ir_e - (Ir_s - (Ir_e - Ir_s)));
            Id_s = Id_e;
            if (Ir_s >= IrMax - 1e-6) break;
          }
        } else {
          const slope = results?.characteristic?.slope;
          const rMax = IrMax;
          for (let r = 0; r <= rMax; r += 0.5) {
            const id = pickupDiff + (typeof slope === 'number' ? slope * Math.max(0, r - 0) : 0);
            pts.push({ I: +r, t: +(+id).toFixed(3) });
          }
        }
        points = pts;
      } else {
        points = [];
      }
    }
  }

  // 注入额外参考线元数据：瞬时阈值与负荷基准（二次值）
  try {
    const results = input?.results || input || {};
    const inst = results?.instantaneousPickup;
    const pm = results?.pickupMultiplier;
    if (typeof inst === 'number' && isFinite(inst)) {
      meta = { ...(meta || {}), instantaneousPickup: +inst };
    }
    if (typeof pm === 'number' && isFinite(pm) && typeof pickup === 'number' && isFinite(pickup) && pm > 0) {
      const baseSec = pickup / pm; // 二次侧负荷基准 = 启动值 / 启动倍数
      if (isFinite(baseSec) && baseSec > 0) meta = { ...(meta || {}), baseLoadSecondary: +(+baseSec).toFixed(3) };
    }
  } catch (_) {}

  return { label, color, pickup, TMS, curveType, points, axesType, meta };
};

// 两曲线选择性校核：返回最差裕度及是否满足最小裕度
export const analyzeSelectivity = (downstream, upstream, options = {}) => {
  const minMargin = typeof options.minMargin === 'number' ? options.minMargin : 0.3; // s
  if (!downstream?.points?.length || !upstream?.points?.length) {
    return { ok: false, worstMargin: null, worstAtI: null, margins: [], intersections: [] };
  }
  // 采用共同 I 轴进行采样（默认使用下级的 I 采样点）
  const margins = [];
  let worstMargin = +Infinity;
  let worstAtI = null;
  const intersections = [];
  downstream.points.forEach(p => {
    const I = p.I;
    const tDn = operateTimeByI(I, downstream.pickup, downstream.TMS, downstream.curveType);
    const tUp = operateTimeByI(I, upstream.pickup, upstream.TMS, upstream.curveType);
    const margin = (tUp === Infinity || tDn === Infinity) ? Infinity : (tUp - tDn);
    margins.push({ I, margin: +(+margin).toFixed(3), tUp, tDn });
    // 记录交点（时间相等附近）
    if (isFinite(tUp) && isFinite(tDn)) {
      const diff = tUp - tDn;
      if (Math.abs(diff) < 0.01) intersections.push(I);
    }
    if (isFinite(margin) && margin < worstMargin) {
      worstMargin = margin;
      worstAtI = I;
    }
  });
  const ok = isFinite(worstMargin) ? worstMargin >= minMargin : false;
  return { ok, worstMargin: isFinite(worstMargin) ? +worstMargin.toFixed(3) : null, worstAtI, margins, intersections };
};

// 推荐上级 TMS 以满足最小裕度（保持下级不变）
export const recommendUpstreamTMS = (downstreamDef, upstreamDef, options = {}) => {
  const minMargin = typeof options.minMargin === 'number' ? options.minMargin : 0.3; // s
  const { pickup: pDn, TMS: tmsDn, curveType: typeDn } = downstreamDef || {};
  const { pickup: pUp, TMS: tmsUp, curveType: typeUp } = upstreamDef || {};
  if (!pDn || !pUp || !tmsDn || !typeDn || !typeUp) {
    return { recommended: null, capped: false, basisPoints: [] };
  }
  // 选择采样范围：使用两者启动值决定范围
  const Imin = Math.max(pDn, pUp) * 1.1;
  const Imax = Math.max(pDn, pUp) * 20;
  const steps = 60;
  const basisPoints = [];
  let requiredMax = 0;
  for (let i = 0; i <= steps; i++) {
    const I = Imin + (Imax - Imin) * (i / steps);
    const tDn = operateTimeByI(I, pDn, tmsDn, typeDn);
    // 求使得 tUp - tDn >= minMargin 的 TMS_up 要求
    const cu = getCurveConstants(typeUp);
    const Mup = I / pUp;
    const denom = Math.pow(Mup, cu.alpha) - 1;
    if (denom <= 0) continue;
    const tmsReq = ((tDn === Infinity ? 0 : (tDn + minMargin)) * denom) / cu.k;
    requiredMax = Math.max(requiredMax, tmsReq);
    basisPoints.push({ I: +I.toFixed(2), tDn: +tDn, denom: +denom.toFixed(6), tmsReq: +(+tmsReq).toFixed(3) });
  }
  let recommended = +requiredMax.toFixed(3);
  let capped = false;
  if (recommended > 1.0) { recommended = 1.0; capped = true; }
  if (recommended < 0.05) recommended = 0.05;
  return { recommended, capped, basisPoints };
};

// 多级选择性校核矩阵：按相邻链路（i 与 i+1）进行校核，并可顺序调整上级 TMS
// 返回 pairwise 结果、总体是否满足、以及顺序调整建议
export const buildSelectivityMatrix = (series = [], options = {}) => {
  const minMarginDefault = typeof options.minMargin === 'number' ? options.minMargin : 0.3;
  const sequentialAdjust = options.sequentialAdjust !== false; // 默认启用顺序调整模拟
  const minMargins = Array.isArray(options.minMargins) ? options.minMargins : undefined;

  // 仅在 I-t 轴的曲线参与矩阵校核
  const chain = (series || []).filter(s => (s && s.axesType === 'I-t' && Array.isArray(s.points) && s.points.length > 0));
  const n = chain.length;
  if (n < 2) {
    return { pairs: [], overallOk: false, adjustments: [], finalSeries: chain };
  }

  // 工作副本（用于模拟调整不影响原对象）
  const work = chain.map(s => ({ ...s }));
  const pairs = [];
  const adjustments = [];

  for (let i = 0; i < n - 1; i++) {
    const dn = work[i];
    const up = work[i + 1];
    const minMargin = minMargins && typeof minMargins[i] === 'number' ? minMargins[i] : minMarginDefault;
    const analysis = analyzeSelectivity(dn, up, { minMargin });

    let recommendation = { recommended: null, capped: false };
    if (!analysis.ok) {
      const rec = recommendUpstreamTMS(
        { pickup: dn.pickup, TMS: dn.TMS, curveType: dn.curveType },
        { pickup: up.pickup, TMS: up.TMS, curveType: up.curveType },
        { minMargin }
      );
      recommendation = rec;
      if (sequentialAdjust && typeof rec.recommended === 'number') {
        const from = up.TMS;
        const to = rec.recommended;
        work[i + 1] = { ...up, TMS: to }; // 应用到工作副本供后续对更上级的校核使用
        adjustments.push({ indexUpstream: i + 1, label: up.label, from, to, capped: !!rec.capped, minMargin });
      }
    }

    pairs.push({
      index: i,
      downstream: dn.label,
      upstream: up.label,
      minMargin,
      ok: !!analysis.ok,
      worstMargin: analysis.worstMargin,
      worstAtI: analysis.worstAtI,
      recommendation
    });
  }

  // 总体满足：所有相邻对均满足或通过顺序调整后再检验一遍
  let overallOk = pairs.every(p => p.ok);
  if (!overallOk && sequentialAdjust && adjustments.length > 0) {
    // 重新按工作副本验证一次
    let okAll = true;
    for (let i = 0; i < n - 1; i++) {
      const dn = work[i];
      const up = work[i + 1];
      const minMargin = minMargins && typeof minMargins[i] === 'number' ? minMargins[i] : minMarginDefault;
      const analysis = analyzeSelectivity(dn, up, { minMargin });
      if (!analysis.ok) { okAll = false; break; }
    }
    overallOk = okAll;
  }

  return { pairs, overallOk, adjustments, finalSeries: work };
};

export default {
  operateTimeByI,
  generateCurvePointsAbsolute,
  buildSeries,
  analyzeSelectivity,
  recommendUpstreamTMS,
  buildSelectivityMatrix,
  toNumber,
  normalizeCurrent,
  normalizeTime,
  normalizeCurveType,
  registerCurveFamily,
  getCurveConstants
};